import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Link,
  Alert,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { LoginRequest } from '../../types/api'
import { StandardInput, StandardButton } from '../ui'
import AuthPageWrapper from './AuthPageWrapper'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { usePublicAppInfo } from '../../hooks/usePublicAppInfo'
import MFAChallenge from '../MFAChallenge'

interface LoginFormProps {
  onShowForgotPassword?: () => void
}

const LoginForm: React.FC<LoginFormProps> = ({ 
  onShowForgotPassword
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, completeMFALogin: _completeMFALogin, isLoading } = useAuth()
  const { appInfo } = usePublicAppInfo()
  
  // Setează titlul paginii dinamic
  useDocumentTitle('Autentificare')
  
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
    remember_me: false
  })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [generalError, setGeneralError] = useState<string>('')
  
  // MFA Challenge state
  const [showMFAChallenge, setShowMFAChallenge] = useState(false)
  const [mfaSessionToken, setMfaSessionToken] = useState('')
  const [mfaAvailableMethods, setMfaAvailableMethods] = useState<Array<{ id: string; type: string; display: string }>>([])

  // Obține URL-ul de redirect din state sau folosește pagina principală
  const from = (location.state as any)?.from?.pathname || '/'

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {}

    // Validare email
    if (!formData.email) {
      newErrors.email = 'Email-ul este obligatoriu'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email-ul nu este valid'
    }

    // Validare parolă
    if (!formData.password) {
      newErrors.password = 'Parola este obligatorie'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Parola trebuie să aibă cel puțin 6 caractere'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof LoginRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }))
    
    // Șterge eroarea pentru câmpul modificat
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
    
    // Șterge eroarea generală când utilizatorul începe să scrie
    if (generalError) {
      setGeneralError('')
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    console.log('LOGIN FORM - handleSubmit called')
    
    if (!validateForm()) {
      return
    }

    try {
      const response = await login(formData)
      console.log('LOGIN FORM - login response:', response)
      
      // Verifică dacă MFA este necesar
      if (response && response.mfa_required) {
        console.log('LOGIN FORM - MFA required, showing challenge')
        setMfaSessionToken(response.session_token!)
        setMfaAvailableMethods(response.available_methods)
        setShowMFAChallenge(true)
      } else {
        console.log('LOGIN FORM - No MFA required, navigating to:', from)
        // Redirectează la pagina dorită sau dashboard
        navigate(from, { replace: true })
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Eroare la autentificare'
      setGeneralError(errorMessage)
      console.error('LOGIN FORM - Error:', error)
    }
  }

  const handleMFASuccess = async (_accessToken: string, _refreshToken: string) => {
    console.log('LOGIN FORM - MFA Success, cookies set by backend')
    // Cookie-urile sunt deja setate de backend în răspunsul de la /mfa/challenge
    // Nu mai apelăm completeMFALogin() - pur și simplu navigăm
    // Silent refresh va încărca user data automat
    
    // Mic delay pentru a da timp browser-ului să proceseze cookie-urile
    setTimeout(() => {
      window.location.href = from
    }, 100)
  }

  const handleMFACancel = () => {
    console.log('LOGIN FORM - MFA Cancelled')
    setShowMFAChallenge(false)
    setMfaSessionToken('')
    setMfaAvailableMethods([])
    setFormData({ email: '', password: '', remember_me: false })
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Afișează MFA Challenge dacă e necesar
  if (showMFAChallenge) {
    return (
      <MFAChallenge
        sessionToken={mfaSessionToken}
        availableMethods={mfaAvailableMethods}
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />
    )
  }

  return (
    <AuthPageWrapper>
      <Paper
        elevation={0}
        sx={{
          padding: 3,
          width: '100%',
          maxWidth: '320px', // Compact, doar cât să încapă formularul
          borderRadius: 8,
          background: 'transparent',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: 'none',
          
          transition: 'border-color 0.3s ease',
          '&:hover': {
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
          
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/logo.svg"
            alt={appInfo?.app_name || 'ZenRIS'}
            sx={{ height: 36 }}
          />
        </Box>

        {generalError && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(211, 47, 47, 0.15)',
              border: '1px solid rgba(211, 47, 47, 0.5)',
              color: '#FFFFFF',
              '& .MuiAlert-icon': {
                color: '#ff5252',
              },
              '& .MuiAlert-message': {
                color: '#FFFFFF',
                fontWeight: 500,
              }
            }}
          >
            {generalError}
          </Alert>
        )}

        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          onKeyDown={(e) => {
            console.log('FORM - Key pressed:', e.key, 'Target:', (e.target as HTMLElement)?.tagName)
            if (e.key === 'Enter') {
              console.log('FORM - Enter key detected')
            }
          }}
          noValidate
        >
          <Box sx={{ mb: 2 }}>
            <StandardInput
              fullWidth
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              errorText={errors.email}
              required
              startIcon={<Email />}
              loading={isLoading}
              placeholder="Email"
              sx={{
                '& .MuiOutlinedInput-root': {
                  paddingLeft: '50px', // Spațiu pentru iconița din stânga
                  
                  // Soluție completă pentru eliminarea background-ului de autocomplete
                  '& input': {
                    '&:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important', // Background negru în loc de transparent
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                      transition: 'background-color 5000s ease-in-out 0s !important', // Delay pentru a preveni flash-ul
                    },
                    '&:-webkit-autofill:hover': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                    },
                    '&:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                    },
                    '&:-webkit-autofill:active': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                    },
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <StandardInput
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange('password')}
              error={!!errors.password}
              errorText={errors.password}
              required
              startIcon={<Lock />}
              endIcon={
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                  disabled={isLoading}
                  size="small"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              }
              loading={isLoading}
              placeholder="Parolă"
              sx={{
                '& .MuiOutlinedInput-root': {
                  paddingLeft: '50px', // Spațiu pentru iconița din stânga
                  
                  // Soluție completă pentru eliminarea background-ului de autocomplete
                  '& input': {
                    '&:-webkit-autofill': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important', // Background negru în loc de transparent
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                      transition: 'background-color 5000s ease-in-out 0s !important', // Delay pentru a preveni flash-ul
                    },
                    '&:-webkit-autofill:hover': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                    },
                    '&:-webkit-autofill:focus': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                    },
                    '&:-webkit-autofill:active': {
                      WebkitBoxShadow: '0 0 0 1000px #000000 inset !important',
                      WebkitTextFillColor: '#FFFFFF !important',
                      backgroundColor: '#000000 !important',
                      boxShadow: '0 0 0 1000px #000000 inset !important',
                    },
                  },
                },
              }}
            />
          </Box>

          {/* Remember Me și Forgot Password pe același rând */}
          <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.remember_me || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, remember_me: e.target.checked }))}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    '&.Mui-checked': {
                      color: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                />
              }
              label="Ține-mă minte"
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                }
              }}
            />
            
            {onShowForgotPassword && (
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  console.log('FORGOT PASSWORD - Link clicked')
                  onShowForgotPassword()
                }}
                disabled={isLoading}
                sx={{ 
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  '&:hover': {
                    color: '#FFFFFF',
                  }
                }}
              >
                Am uitat parola
              </Link>
            )}
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <StandardButton
              type="submit"
              fullWidth
              variant="ghost"
              size="large"
              loading={isLoading}
              disabled={isLoading}
            >
              Autentificare
            </StandardButton>
          </Box>

        </Box>
      </Paper>
    </AuthPageWrapper>
  )
}

export default LoginForm
